import { c as _c } from "react/compiler-runtime";
import { useInfiniteQuery, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { getCheckHospitalCollectionClient, getHospitalFavoritesClient, getHospitalInfoClient, getHospitalReviewAnalysisClient, getHospitalReviewClient, getHospitalTreatmentCaseSummaryClient, getReviewDetailClient, getSubjectsAnalysisClient } from "@/entities/hospital/api/hospital.client";
import { ApiResponse } from "@/shared/types/interface/api";
import { GetHospitalFavoriteResponse, GetHospitalReviewResponse } from "@/shared/types/type/hospital";
import { GetHospitalInfoResponseType } from "@/entities/hospital/api/types";
import { HospitalReviewSortEnum, ReviewAnalysisResponse, TreatmentCaseSummaryResponse } from "@/entities/hospital/types/type";
import { hospitalReviewQueryKey } from "@/entities/hospital/lib/review-query-key";
import { PRESERVE_CACHED_DATA_META } from "@/shared/lib/query-error-policy";
import Cookies from "js-cookie";
import { ReviewDetailResponseDTO, SubjectsAnalysisResponseDTO } from "../model/types";
export const useQueryHospitalInfo = (hospitalOriginId, locale, options) => {
  const $ = _c(8);
  let t0;
  let t1;
  if ($[0] !== hospitalOriginId || $[1] !== locale) {
    t0 = ["hospitalDetail", hospitalOriginId, locale];
    t1 = () => getHospitalInfoClient(hospitalOriginId, locale);
    $[0] = hospitalOriginId;
    $[1] = locale;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0 || $[6] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      staleTime: 0,
      gcTime: 0,
      ...options
    };
    $[4] = options;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
};
export const useQueryHospitalFavoriteState = (hospitalOriginId, enabled) => {
  const $ = _c(9);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["hospitalFavorites"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== hospitalOriginId) {
    t1 = response => response.result.some(hospital => hospital.hospitalOriginId === hospitalOriginId);
    $[1] = hospitalOriginId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== enabled || $[4] !== hospitalOriginId) {
    t2 = enabled && Number.isFinite(hospitalOriginId);
    $[3] = enabled;
    $[4] = hospitalOriginId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: getHospitalFavoritesClient,
      select: t1,
      enabled: t2,
      staleTime: 0,
      gcTime: 0
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
};
export const useQueryHospitalFavorites = t0 => {
  const $ = _c(3);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["hospitalFavorites"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== enabled) {
    t2 = {
      queryKey: t1,
      queryFn: getHospitalFavoritesClient,
      enabled,
      staleTime: 0,
      gcTime: 0
    };
    $[1] = enabled;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return useQuery(t2);
};
export const useInfiniteQueryHospitalReview = (hospitalOriginId, sortEnum, locale) => {
  const $ = _c(10);
  let t0;
  if ($[0] !== hospitalOriginId || $[1] !== locale || $[2] !== sortEnum) {
    t0 = hospitalReviewQueryKey(hospitalOriginId, sortEnum, locale);
    $[0] = hospitalOriginId;
    $[1] = locale;
    $[2] = sortEnum;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== hospitalOriginId || $[5] !== sortEnum) {
    t1 = async t2 => {
      const {
        pageParam: t3
      } = t2;
      const pageParam = t3 === undefined ? 1 : t3;
      return await getHospitalReviewClient(hospitalOriginId, pageParam as number, sortEnum);
    };
    $[4] = hospitalOriginId;
    $[5] = sortEnum;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t0 || $[8] !== t1) {
    t2 = {
      queryKey: t0,
      meta: PRESERVE_CACHED_DATA_META,
      queryFn: t1,
      initialPageParam: 1,
      getNextPageParam: _temp,
      placeholderData: _temp2
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return useInfiniteQuery(t2);
};
export const useCheckHospitalCollection = (hospitalId, locale) => {
  const $ = _c(9);
  let t0;
  if ($[0] !== hospitalId) {
    t0 = ["isCollection", hospitalId];
    $[0] = hospitalId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== hospitalId || $[3] !== locale) {
    t1 = () => getCheckHospitalCollectionClient(hospitalId, locale);
    $[2] = hospitalId;
    $[3] = locale;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = Cookies.get("loggedIn");
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t0 || $[7] !== t1) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2 === "1"
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
};
export const useQueryHospitalTreatmentCaseSummary = hospitalOriginId => {
  const $ = _c(7);
  let t0;
  if ($[0] !== hospitalOriginId) {
    t0 = ["hospitalTreatmentCaseSummary", hospitalOriginId];
    $[0] = hospitalOriginId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== hospitalOriginId) {
    t1 = () => getHospitalTreatmentCaseSummaryClient(hospitalOriginId);
    $[2] = hospitalOriginId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = {
      queryKey: t0,
      meta: PRESERVE_CACHED_DATA_META,
      queryFn: t1
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useQuery(t2);
};
export const useQueryHospitalReviewAnalysis = hospitalOriginId => {
  const $ = _c(7);
  let t0;
  if ($[0] !== hospitalOriginId) {
    t0 = ["hospitalReviewAnalysis", hospitalOriginId];
    $[0] = hospitalOriginId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== hospitalOriginId) {
    t1 = () => getHospitalReviewAnalysisClient(hospitalOriginId);
    $[2] = hospitalOriginId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = {
      queryKey: t0,
      meta: PRESERVE_CACHED_DATA_META,
      queryFn: t1
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useQuery(t2);
};
export const useQueryHospitalSubjectsAnalysis = hospitalOriginId => {
  const $ = _c(7);
  let t0;
  if ($[0] !== hospitalOriginId) {
    t0 = ["hospitalSubjectsAnalysis", hospitalOriginId];
    $[0] = hospitalOriginId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== hospitalOriginId) {
    t1 = () => getSubjectsAnalysisClient(hospitalOriginId);
    $[2] = hospitalOriginId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = {
      queryKey: t0,
      meta: PRESERVE_CACHED_DATA_META,
      queryFn: t1
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useQuery(t2);
};
export const useInfiniteQueryReviewDetail = (hospitalOriginId, page, size, sort, subject, major, minor, group) => {
  const $ = _c(20);
  let t0;
  if ($[0] !== group || $[1] !== hospitalOriginId || $[2] !== major || $[3] !== minor || $[4] !== page || $[5] !== size || $[6] !== sort || $[7] !== subject) {
    t0 = ["reviewDetail", hospitalOriginId, page, size, sort, subject, major, minor, group];
    $[0] = group;
    $[1] = hospitalOriginId;
    $[2] = major;
    $[3] = minor;
    $[4] = page;
    $[5] = size;
    $[6] = sort;
    $[7] = subject;
    $[8] = t0;
  } else {
    t0 = $[8];
  }
  let t1;
  if ($[9] !== group || $[10] !== hospitalOriginId || $[11] !== major || $[12] !== minor || $[13] !== size || $[14] !== sort || $[15] !== subject) {
    t1 = async t2 => {
      const {
        pageParam: t3
      } = t2;
      const pageParam = t3 === undefined ? 1 : t3;
      return await getReviewDetailClient(hospitalOriginId, pageParam as number, size, sort, subject, major, minor, group);
    };
    $[9] = group;
    $[10] = hospitalOriginId;
    $[11] = major;
    $[12] = minor;
    $[13] = size;
    $[14] = sort;
    $[15] = subject;
    $[16] = t1;
  } else {
    t1 = $[16];
  }
  let t2;
  if ($[17] !== t0 || $[18] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      initialPageParam: 1,
      getNextPageParam: _temp3
    };
    $[17] = t0;
    $[18] = t1;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  return useInfiniteQuery(t2);
};
function _temp(lastPage, pages) {
  if (lastPage.status !== 200) {
    return;
  }
  if (!lastPage.result.reviewList.hasNext) {
    return;
  }
  return pages.length + 1;
}
function _temp2(previousData) {
  return previousData;
}
function _temp3(lastPage, pages) {
  if (lastPage.totalElements === 0) {
    return;
  }
  if (!lastPage.hasNext) {
    return;
  }
  return pages.length + 1;
}